import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Building2, Users, User, Check, ArrowRight, Shield, AlertCircle } from 'lucide-react';
import AppLayout from '../components/AppLayout';

const PLANS = {
  brand_growth: {
    name: "Brand Growth",
    user_type: "brand",
    price: 99,
    images: 200,
    features: [
      "200 AI try-on images/month",
      "Upload up to 25 clothing items",
      "Reduce returns by up to 30%",
      "Save 20% on shipping costs",
      "Basic analytics dashboard",
      "Connect with 2 influencers"
    ]
  },
  brand_starter: {
    name: "Brand Starter",
    user_type: "brand",
    price: 199,
    images: 500,
    features: [
      "500 AI try-on images/month",
      "Upload up to 50 clothing items",
      "Reduce returns by up to 40%",
      "Save 25% on shipping costs",
      "Basic analytics dashboard",
      "Connect with 5 influencers"
    ]
  },
  brand_enterprise: {
    name: "Brand Enterprise",
    user_type: "brand",
    price: 399,
    images: 1000,
    popular: true,
    features: [
      "1000 AI try-on images/month",
      "Upload up to 500 clothing items",
      "Reduce returns by up to 50%",
      "Save 35% on shipping costs",
      "Advanced analytics & ROI tracking",
      "Unlimited influencer collaborations",
      "Priority support"
    ]
  },
  influencer_starter: {
    name: "Influencer Starter",
    user_type: "influencer",
    price: 19,
    images: 50,
    features: [
      "50 AI try-on images/month",
      "Upload up to 20 looks",
      "Personal profile page",
      "Receive brand campaign invites",
      "Earn through brand campaigns",
      "Basic analytics"
    ]
  },
  influencer_pro: {
    name: "Influencer Pro",
    user_type: "influencer",
    price: 79.99,
    images: 200,
    popular: true,
    features: [
      "200 AI try-on images/month",
      "Upload up to 100 looks",
      "Premium profile page",
      "Advanced affiliate tracking",
      "Brand collaboration requests",
      "Detailed analytics dashboard",
      "Priority listing in search"
    ]
  },
  personal: {
    name: "Personal",
    user_type: "personal",
    price: 9,
    images: 20,
    features: [
      "20 AI try-on images/month",
      "Try clothes before you buy",
      "Download generated images",
      "Personal use only"
    ]
  }
};

const SubscribePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const [selectedType, setSelectedType] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [promoInfo, setPromoInfo] = useState(null);

  // Canceled users are allowed to resubscribe; treat them like anonymous visitors for plan pick.
  const isCanceled = user?.subscription_status === 'canceled';
  const showSubscribeUI = !isAuthenticated || isCanceled;

  // Pre-select user type from URL query parameter
  useEffect(() => {
    const typeParam = searchParams.get('type');
    const planParam = searchParams.get('plan');
    const promoParam = searchParams.get('promo');
    
    // Persist promo code to localStorage so it survives redirects
    if (promoParam) {
      localStorage.setItem('vf_promo_code', promoParam);
      // Look up the promo's real duration so the banner shows accurate copy
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      fetch(`${apiUrl}/api/promo/info?code=${encodeURIComponent(promoParam)}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (d) setPromoInfo(d); })
        .catch(() => {});
    }
    
    if (planParam && PLANS[planParam]) {
      // Promo link: auto-select the plan directly
      const plan = PLANS[planParam];
      handleTypeSelect(plan.user_type);
      setSelectedPlan(planParam);
    } else if (typeParam && ['brand', 'influencer', 'personal'].includes(typeParam)) {
      handleTypeSelect(typeParam);
    } else if (isCanceled && user?.user_type) {
      // Canceled users are locked to plans within their existing user_type
      // (data-model integrity — portfolio / collab schemas differ per type).
      // Skip the account-type picker; they can still upgrade / downgrade
      // within their type on the next step.
      handleTypeSelect(user.user_type);
    }
    // Note: canceled users are NOT auto-preselected into their prior PLAN —
    // only their user_type is locked. They pick any tier within it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isCanceled, user?.plan_id]);

  // Auto-select plan when there's only one option (Personal)
  const handleTypeSelect = (type) => {
    setSelectedType(type);
    const plansForType = Object.entries(PLANS).filter(([_, plan]) => plan.user_type === type);
    if (plansForType.length === 1) {
      // Auto-select the only plan available
      setSelectedPlan(plansForType[0][0]);
    } else {
      // Reset selection for types with multiple plans
      setSelectedPlan(null);
    }
  };

  const userTypes = [
    {
      type: 'brand',
      icon: Building2,
      title: 'Brand',
      description: 'Fashion brands & retailers looking to reduce returns and showcase products',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      type: 'influencer',
      icon: Users,
      title: 'Influencer',
      description: 'Content creators who want to showcase outfits and earn through brand campaigns',
      color: 'from-purple-500 to-pink-600'
    },
    {
      type: 'personal',
      icon: User,
      title: 'Personal',
      description: 'Shoppers who want to try clothes virtually before buying',
      color: 'from-rose-500 to-orange-500'
    }
  ];

  const getPlansForType = (type) => {
    return Object.entries(PLANS).filter(([_, plan]) => plan.user_type === type);
  };

  if (isAuthenticated && !isCanceled) {
    return (
      <AppLayout>
        <div className="min-h-[calc(100vh-200px)] bg-gradient-to-br from-rose-50 via-white to-orange-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">You're already subscribed!</h2>
            <p className="text-gray-600 mb-6">You can manage your subscription or start trying on clothes.</p>
            <div className="flex flex-col gap-3">
              <Link to="/dashboard" className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl font-semibold">
                Go to Dashboard <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link to="/subscription" className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50">
                Manage Subscription
              </Link>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="min-h-[calc(100vh-200px)] bg-gradient-to-br from-rose-50 via-white to-orange-50">
        {searchParams.get('promo') && (
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-center py-3 px-4" data-testid="promo-banner">
            <span className="font-semibold">You've been invited!</span>{' '}
            {promoInfo && promoInfo.valid === false ? (
              <>This promo link has already been used or expired — you can still pick a plan and register at the standard price.</>
            ) : promoInfo && promoInfo.duration_months >= 2 ? (
              <>Your first <strong>{promoInfo.duration_months} months</strong> are free — just pick your plan and register.</>
            ) : (
              <>Your first month is free — just pick your plan and register.</>
            )}
          </div>
        )}
        {isCanceled && typeof user?.data_recovery_days_left === 'number' && user.data_recovery_days_left > 0 && (
          <div
            className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-center py-3 px-4"
            data-testid="subscribe-recovery-banner"
          >
            <span className="font-semibold">
              {user.data_recovery_days_left} {user.data_recovery_days_left === 1 ? 'day' : 'days'} left to keep your data
            </span>{' '}
            — resubscribe to any plan and your portfolio, try-on history, collaborations and chats are restored intact.
          </div>
        )}
        {isCanceled && typeof user?.data_recovery_days_left === 'number' && user.data_recovery_days_left === 0 && (
          <div
            className="bg-rose-600 text-white text-center py-3 px-4"
            data-testid="subscribe-recovery-expired-banner"
          >
            <span className="font-semibold">Data recovery window has closed.</span>{' '}
            You can still resubscribe and start fresh.
          </div>
        )}
        <div className="max-w-7xl mx-auto px-4 py-12">
          {/* Step 1: Choose User Type */}
          {!selectedType ? (
            <>
              <div className="text-center mb-12">
                <h1 className="text-4xl font-bold text-gray-800 mb-4">Choose Your Account Type</h1>
                <p className="text-lg text-gray-600">Select the plan that best fits your needs</p>
              </div>

              <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                {userTypes.map(({ type, icon: Icon, title, description, color }) => (
                  <button
                    key={type}
                    onClick={() => handleTypeSelect(type)}
                    className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all text-left group hover:-translate-y-1"
                    data-testid={`select-type-${type}`}
                  >
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${color} flex items-center justify-center mb-6`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">{title}</h3>
                    <p className="text-gray-600 mb-6">{description}</p>
                    <div className="flex items-center text-gray-800 font-semibold group-hover:text-rose-500 transition-colors">
                      View Plans <ArrowRight className="ml-2 w-5 h-5" />
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : (
            /* Step 2: Choose Plan */
            <>
              <div className="text-center mb-12">
                {isCanceled ? (
                  <p className="text-sm text-gray-500 mb-4" data-testid="canceled-type-locked-note">
                    Your account is a <strong className="capitalize">{selectedType}</strong> account. Pick any {selectedType} plan below —
                    to switch account type you&apos;ll need to create a new account.
                  </p>
                ) : (
                  <button 
                    onClick={() => { setSelectedType(null); setSelectedPlan(null); }}
                    className="text-gray-500 hover:text-gray-700 mb-4 inline-flex items-center"
                    data-testid="back-to-types-btn"
                  >
                    ← Back to account types
                  </button>
                )}
                <h1 className="text-4xl font-bold text-gray-800 mb-4">
                  Choose Your {selectedType.charAt(0).toUpperCase() + selectedType.slice(1)} Plan
                </h1>
              </div>

              <div className={`grid ${selectedType === 'personal' ? 'max-w-md mx-auto' : selectedType === 'brand' ? 'md:grid-cols-3 max-w-6xl mx-auto' : 'md:grid-cols-2 max-w-4xl mx-auto'} gap-8`}>
                {getPlansForType(selectedType).map(([planId, plan]) => {
                  const isSelected = selectedPlan === planId;
                  
                  return (
                    <div 
                      key={planId}
                      onClick={() => setSelectedPlan(planId)}
                      className={`bg-white rounded-2xl p-8 shadow-lg relative cursor-pointer transition-all duration-200 flex flex-col ${
                        isSelected 
                          ? 'ring-2 ring-rose-500 shadow-xl scale-[1.02]' 
                          : plan.popular
                            ? 'ring-2 ring-rose-200 hover:ring-rose-300 hover:shadow-xl'
                            : 'hover:shadow-xl hover:ring-2 hover:ring-gray-200'
                      }`}
                      data-testid={`plan-${planId}`}
                    >
                      {plan.popular && !isSelected && (
                        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-rose-400 to-orange-400 text-white px-4 py-1 rounded-full text-sm font-semibold">
                          Most Popular
                        </div>
                      )}
                      {isSelected && (
                        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-rose-500 to-orange-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                          Selected
                        </div>
                      )}
                      
                      <h3 className="text-2xl font-bold text-gray-800 mb-2">{plan.name}</h3>
                      <div className="flex items-baseline mb-6">
                        <span className="text-4xl font-bold text-gray-800">${plan.price}</span>
                        <span className="text-gray-500 ml-2">/month</span>
                      </div>
                      <p className="text-gray-600 mb-6">{plan.images} AI try-on images per month</p>
                      
                      <ul className="space-y-3 mb-8 flex-grow">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start">
                            <Check className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5 text-green-500" />
                            <span className="text-gray-600">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const promoParam = searchParams.get('promo');
                          const promoQuery = promoParam ? `&promo=${promoParam}` : '';
                          navigate(`/register?plan=${planId}&type=${selectedType}${promoQuery}`);
                        }}
                        className={`w-full py-4 rounded-xl font-semibold transition-all mt-auto ${
                          isSelected
                            ? 'bg-gradient-to-r from-rose-500 to-orange-500 text-white'
                            : 'bg-rose-50 text-rose-600 border border-rose-200 hover:bg-gradient-to-r hover:from-rose-500 hover:to-orange-500 hover:text-white hover:border-transparent'
                        }`}
                        data-testid={`get-started-${planId}`}
                      >
                        Get Started
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Benefits Section */}
              {selectedType === 'brand' && (
                <div className="mt-16 bg-white rounded-2xl p-8 shadow-lg max-w-4xl mx-auto">
                  <h3 className="text-2xl font-bold text-gray-800 mb-6">Why Brands Choose VirtualFit</h3>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="text-center p-4">
                      <div className="text-4xl font-bold text-rose-500 mb-2">40-50%</div>
                      <p className="text-gray-600">Reduction in returns</p>
                    </div>
                    <div className="text-center p-4">
                      <div className="text-4xl font-bold text-rose-500 mb-2">25-35%</div>
                      <p className="text-gray-600">Savings on shipping</p>
                    </div>
                    <div className="text-center p-4">
                      <div className="text-4xl font-bold text-rose-500 mb-2">3x</div>
                      <p className="text-gray-600">Higher conversion rate</p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default SubscribePage;
